import { useMemo, type ReactNode } from "react";
import { CircleArrowUp, Info, Lightbulb, Sparkles, ThumbsUp, TriangleAlert, Trophy } from "lucide-react";
import { ChartCard } from "./ui";
import { cn } from "@/lib/utils";

const TIP_TONES = {
  warning: "bg-warning-500/15 text-warning-700",
  success: "bg-success-500/15 text-success-700",
  info: "bg-info-500/15 text-info-700",
};

const ChatbotTips = ({ dailyAnswerRate, failingQuestions, confidenceBuckets }: any) => {
  const tips = useMemo(() => {
    const result: { icon: ReactNode; type: string; title: string; text: string }[] = [];

    // Calculate overall answer rate
    if (dailyAnswerRate && dailyAnswerRate.length > 0) {
      const totalQueries = dailyAnswerRate.reduce((s, d) => s + (d.total_queries || 0), 0);
      const totalAnswered = dailyAnswerRate.reduce((s, d) => s + (d.answered || 0), 0);
      const overallRate = totalQueries > 0 ? (totalAnswered / totalQueries) * 100 : 0;
      const unansweredPct = (100 - overallRate).toFixed(0);

      if (overallRate < 80) {
        result.push({
          icon: <TriangleAlert />,
          type: "warning",
          title: "Answer rate is below 80%",
          text: `Your chatbot couldn\u2019t answer ${unansweredPct}% of guest questions. Review the unanswered questions above and add FAQs for common topics.`,
        });
      } else if (overallRate >= 90) {
        result.push({
          icon: <Trophy />,
          type: "success",
          title: "Great answer rate!",
          text: `Your chatbot is answering ${overallRate.toFixed(0)}% of guest questions. Keep your FAQs up to date to maintain this.`,
        });
      }
    }

    // Check failing questions
    if (failingQuestions && failingQuestions.length > 0) {
      const topQ = failingQuestions[0];
      if (failingQuestions.length >= 5) {
        result.push({
          icon: <Lightbulb />,
          type: "info",
          title: `${failingQuestions.length} topics guests can\u2019t get help with`,
          text: `Guests frequently ask about "${topQ.question}" (${topQ.count} times). Adding FAQs for these topics will improve your chatbot\u2019s performance.`,
        });
      } else if (failingQuestions.length > 0) {
        result.push({
          icon: <Lightbulb />,
          type: "info",
          title: "Some questions need your attention",
          text: `"${topQ.question}" was asked ${topQ.count} times without an answer. Consider adding it to your FAQs.`,
        });
      }
    }

    // Check confidence / answer quality
    if (confidenceBuckets && confidenceBuckets.length === 4) {
      const totalBucket = confidenceBuckets.reduce((s, b) => s + (b.count || 0), 0);
      const lowQuality = (confidenceBuckets[0]?.count || 0) + (confidenceBuckets[1]?.count || 0);
      const highQuality = (confidenceBuckets[2]?.count || 0) + (confidenceBuckets[3]?.count || 0);

      if (totalBucket > 0 && lowQuality / totalBucket > 0.4) {
        result.push({
          icon: <CircleArrowUp />,
          type: "warning",
          title: "Answer quality could be improved",
          text: `${((lowQuality / totalBucket) * 100).toFixed(0)}% of answers have low quality scores. Review and expand your FAQ content to help the AI give better responses.`,
        });
      } else if (totalBucket > 0 && highQuality / totalBucket > 0.7) {
        result.push({
          icon: <ThumbsUp />,
          type: "success",
          title: "Answer quality is strong",
          text: `${((highQuality / totalBucket) * 100).toFixed(0)}% of answers are rated Good or Excellent. Your FAQ content is working well.`,
        });
      }
    }

    // If no tips generated, show a default
    if (result.length === 0) {
      result.push({
        icon: <Info />,
        type: "info",
        title: "Keep monitoring",
        text: "Not enough data yet to generate specific tips. Check back once your chatbot has handled more guest conversations.",
      });
    }

    return result;
  }, [dailyAnswerRate, failingQuestions, confidenceBuckets]);
  return (
    <ChartCard icon={<Sparkles />} title="Improve Your Chatbot" description="Simple tips based on the numbers above." className="chatbot-tips-section">
      <ul className="chatbot-tips-list grid gap-3 md:grid-cols-2">
        {tips.map((tip, idx) => (
          <li key={idx} className={cn("chatbot-tip flex items-start gap-3 rounded-lg border border-border bg-background p-3", `chatbot-tip-${tip.type}`)}>
            <span className={cn("chatbot-tip-icon flex size-8 shrink-0 items-center justify-center rounded-lg [&_svg]:size-4", TIP_TONES[tip.type] || TIP_TONES.info)} aria-hidden="true">
              {tip.icon}
            </span>
            <div className="chatbot-tip-body min-w-0">
              <p className="chatbot-tip-title text-sm font-semibold">{tip.title}</p>
              <p className="chatbot-tip-text text-sm text-muted-foreground">{tip.text}</p>
            </div>
          </li>
        ))}
      </ul>
    </ChartCard>
  );
};

export default ChatbotTips;
